/**
 * 운영자 매뉴얼 (3-pane 셸).
 *
 * `/manual` 전체 매뉴얼 목록 → `/manual/:manual` 매뉴얼 표지 → `/manual/:manual/:section/:slug` 문서.
 * 매뉴얼은 여러 개다(Composer가 첫 번째일 뿐) — 최상위 단위는 섹션이 아니라 **매뉴얼**이다.
 * 콘텐츠 정본은 레포 마크다운(`docs/manual/`) — 집필 규약은 `docs/manual/README.md`.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { buttonClassName } from '@safetyhub/ui';
import { manualDocDir } from './manualAssets';
import { getDoc, getManual, manuals, type ManualNode } from './manualContent';
import ManualDoc, { ManualBody } from './ManualDoc';
import ManualSearch from './ManualSearch';
import ManualToc from './ManualToc';
import { computeTailSpacer } from './manualTocGeometry';
import { getStale, needsReview, regenerateCountOf, staleCountOf } from './manualStale';

/** 검색 단축키 힌트 — mac은 ⌘, 그 외는 Ctrl (표시만 다르고 동작은 둘 다 지원). */
const SEARCH_HOTKEY_HINT =
  typeof navigator !== 'undefined' && /Mac|iP(hone|ad|od)/.test(navigator.platform)
    ? '⌘K'
    : 'Ctrl K';

/**
 * 본문 끝 여백 높이 — 마지막 제목이 목차 활성 띠까지 올라올 수 있게 한다.
 *
 * 이게 없으면 문서 끝에서 스크롤이 멈춰, **마지막 화면 한 장 안의 제목들이 활성 구간을 못 갖는다**
 * (2026-07-23 사용자 제보: 끝까지 내려도 중간 제목에 표시가 멈춤). 필요한 만큼만 계산하므로
 * 본문이 이미 충분히 길면 0이 되어 빈 공간이 생기지 않는다.
 */
function useTailSpacer(containerRef: React.RefObject<HTMLElement | null>, docKey: string): number {
  const [spacer, setSpacer] = useState(0);

  useEffect(() => {
    let frame = 0;

    const measure = () => {
      frame = 0;
      const root = containerRef.current;
      if (!root) return;

      const headings = root.querySelectorAll('h2[id], h3[id]');
      const last = headings[headings.length - 1];
      if (!last) {
        setSpacer(0);
        return;
      }

      // 여백 자신을 뺀 본문 높이로 재야 한다 — 포함해서 재면 값이 누적돼 여백이 계속 자란다.
      const body = root.querySelector('[data-manual-body]') ?? root;
      const contentBelowLastHeading =
        body.getBoundingClientRect().bottom - last.getBoundingClientRect().top;

      setSpacer(
        computeTailSpacer({ viewport: window.innerHeight, contentBelowLastHeading }),
      );
    };

    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(measure);
    };

    schedule();
    window.addEventListener('resize', schedule, { passive: true });
    return () => {
      window.removeEventListener('resize', schedule);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [containerRef, docKey]);

  return spacer;
}

/** 좌측 트리 — **현재 매뉴얼**의 섹션/문서만. 상단에 전체 목록으로 나가는 링크. */
function SectionTree({
  manual,
  currentSection,
  currentSlug,
  onNavigate,
}: {
  manual: ManualNode;
  currentSection?: string;
  currentSlug?: string;
  onNavigate?: () => void;
}) {
  return (
    <nav aria-label="매뉴얼 목차" className="text-sm">
      <Link
        to="/manual"
        onClick={onNavigate}
        className="mb-1 block text-xs text-gray-500 hover:text-red-600"
      >
        ← 전체 매뉴얼
      </Link>
      <Link
        to={`/manual/${manual.id}`}
        onClick={onNavigate}
        className="mb-3 block font-semibold text-gray-900 hover:text-red-600"
      >
        {manual.title}
      </Link>
      {manual.sections.length === 0 && (
        <p className="text-gray-500">아직 등록된 문서가 없습니다.</p>
      )}
      <ul className="space-y-4">
        {manual.sections.map((section) => (
          <li key={section.id}>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
              {section.label}
            </p>
            <ul className="space-y-0.5">
              {section.docs.map((doc) => {
                const isActive = doc.section === currentSection && doc.slug === currentSlug;
                return (
                  <li key={doc.slug}>
                    <Link
                      to={`/manual/${doc.manual}/${doc.section}/${doc.slug}`}
                      onClick={onNavigate}
                      title={doc.summary || undefined}
                      className={`block rounded px-2 py-1 ${
                        isActive
                          ? 'bg-red-50 font-medium text-red-700'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {doc.title}
                      {doc.incomplete && (
                        <span className="ml-1 text-red-500" title="프론트매터 필수 키 누락">
                          !
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/** 매뉴얼 › 문서 위치 표시. */
function Breadcrumb({ manual, docTitle }: { manual?: ManualNode; docTitle?: string }) {
  return (
    <nav aria-label="위치" className="mb-3 flex flex-wrap items-center gap-1 text-xs text-gray-500">
      <Link to="/manual" className="hover:text-gray-700 hover:underline">
        매뉴얼
      </Link>
      {manual && (
        <>
          <span aria-hidden>›</span>
          <Link to={`/manual/${manual.id}`} className="hover:text-gray-700 hover:underline">
            {manual.title}
          </Link>
        </>
      )}
      {docTitle && (
        <>
          <span aria-hidden>›</span>
          <span className="text-gray-700">{docTitle}</span>
        </>
      )}
    </nav>
  );
}

/**
 * 정비 필요 문서 수 — 0이면 아무것도 그리지 않는다.
 *
 * 드리프트 산출물(`docs/manual/_generated/stale.json`)이 없으면 항상 0이라 표시가 사라진다.
 * 이 표시는 부가 정보이므로 **없다고 목록이 달라 보이면 안 된다**.
 *
 * ⚠️ **두 수를 갈라 보여 준다.** 「정비 필요」는 *사람이 읽고 판정할 문서*의 수(`staleCountOf` —
 * 생성물 제외)이고, 생성물은 처방이 `pnpm manual:reference`라 **「재생성 대기」**로 따로 센다.
 * 한 수로 합치면 내용이 정확해도 경고가 남는 생성물 때문에 **0이 될 수 없는 수**가 되어,
 * 보는 사람이 그 수를 무시하게 된다(`scripts/manual/drift.mjs`의 두 구획과 같은 뜻).
 */
function StaleCount({ manual }: { manual: string }) {
  const count = staleCountOf(manual);
  const regenerate = regenerateCountOf(manual);
  if (count === 0 && regenerate === 0) return null;
  return (
    <>
      {count > 0 && (
        <span
          className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800"
          title="설명하는 코드가 문서보다 나중에 바뀐 문서입니다"
        >
          정비 필요 {count}건
        </span>
      )}
      {regenerate > 0 && (
        <span
          className="rounded bg-emerald-100 px-1.5 py-0.5 text-xs font-medium text-emerald-800"
          title="코드에서 자동 생성되는 문서입니다 — pnpm manual:reference로 다시 만듭니다"
        >
          재생성 대기 {regenerate}건
        </span>
      )}
    </>
  );
}

/** `/manual` — 전체 매뉴얼 목록. */
function ManualHome() {
  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900">매뉴얼</h1>
      <p className="mt-2 text-gray-600">
        운영 도구별 사용 설명서입니다. 보려는 매뉴얼을 고르세요.
      </p>

      {manuals.length === 0 ? (
        <p className="mt-8 rounded-card border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center text-gray-500">
          아직 등록된 매뉴얼이 없습니다.
        </p>
      ) : (
        <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {manuals.map((manual) => (
            <Link
              key={manual.id}
              to={`/manual/${manual.id}`}
              className="block rounded-card border border-gray-200 bg-white p-4 hover:border-red-300 hover:bg-red-50/30"
            >
              <h2 className="text-base font-semibold text-gray-900">{manual.title}</h2>
              {manual.summary && <p className="mt-1 text-sm text-gray-600">{manual.summary}</p>}
              <p className="mt-3 flex items-center gap-2 text-xs text-gray-400">
                <span>문서 {manual.docs.length}편</span>
                <StaleCount manual={manual.id} />
              </p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/** `/manual/:manual` — 매뉴얼 표지(index.md 본문) + 섹션 카드. */
function ManualCoverPage({ manual }: { manual: ManualNode }) {
  return (
    <div>
      <Breadcrumb manual={manual} />
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold text-gray-900">{manual.title}</h1>
        <StaleCount manual={manual.id} />
      </div>
      {manual.summary && <p className="mt-2 text-gray-600">{manual.summary}</p>}

      {manual.cover ? (
        <div className="mt-4 max-w-3xl text-[15px]">
          <ManualBody
            manual={manual.id}
            docDir={manualDocDir(manual.cover.path)}
            body={manual.cover.body}
          />
        </div>
      ) : (
        <p className="mt-4 rounded-card border border-dashed border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          이 매뉴얼에는 표지(<code>index.md</code>)가 없습니다. 제목·설명이 매뉴얼 id로 표시됩니다.
        </p>
      )}

      {manual.sections.length === 0 ? (
        <p className="mt-8 rounded-card border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center text-gray-500">
          아직 등록된 문서가 없습니다.
        </p>
      ) : (
        <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {manual.sections.map((section) => (
            <section
              key={section.id}
              className="rounded-card border border-gray-200 bg-white p-4 hover:border-gray-300"
            >
              <h2 className="flex items-baseline justify-between text-base font-semibold text-gray-900">
                {section.label}
                <span className="text-xs font-normal text-gray-400">{section.docs.length}편</span>
              </h2>
              <ul className="mt-3 space-y-1 text-sm">
                {section.docs.slice(0, 5).map((doc) => (
                  <li key={doc.slug}>
                    <Link
                      to={`/manual/${doc.manual}/${doc.section}/${doc.slug}`}
                      className="text-brand-600 hover:text-brand-800 hover:underline"
                    >
                      {doc.title}
                    </Link>
                    {/* 생성물은 ● 대신 ◆ — 영구 경고라 ●로 두면 목록이 늘 점으로 덮인다. */}
                    {needsReview(doc.manual, doc.section, doc.slug) ? (
                      <span
                        className="ml-1 text-amber-600"
                        title="설명하는 코드가 문서보다 나중에 바뀌었습니다"
                      >
                        ●
                      </span>
                    ) : getStale(doc.manual, doc.section, doc.slug) ? (
                      <span
                        className="ml-1 text-emerald-600"
                        title="자동 생성 문서 — 마지막 생성 이후 코드가 바뀌었습니다(재생성 대기)"
                      >
                        ◆
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
              {section.docs.length > 5 && (
                <p className="mt-2 text-xs text-gray-400">외 {section.docs.length - 5}편</p>
              )}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

/** 없는 매뉴얼/문서 — throw 없이 안내 + 목록 링크. */
function NotFound({ what, target, backTo }: { what: string; target: string; backTo: string }) {
  return (
    <div className="max-w-xl">
      <h1 className="text-2xl font-bold text-gray-900">{what}를 찾을 수 없습니다</h1>
      <p className="mt-2 text-gray-600">
        요청한 {what}(<code className="rounded bg-gray-100 px-1.5 py-0.5 text-sm">{target}</code>
        )가 없습니다. 주소가 바뀌었거나 아직 작성되지 않았을 수 있습니다.
      </p>
      <Link
        to={backTo}
        className={buttonClassName('primary', 'md', 'mt-4')}
      >
        매뉴얼 목록으로
      </Link>
    </div>
  );
}

export default function ManualPage() {
  const { manual: manualId, section, slug } = useParams<{
    manual: string;
    section: string;
    slug: string;
  }>();
  const manual = manualId ? getManual(manualId) : undefined;
  const doc = manualId && section && slug ? getDoc(manualId, section, slug) : undefined;
  const contentRef = useRef<HTMLDivElement>(null);
  // 좁은 폭에서 좌측 트리는 기본 접힘 (본문 우선)
  const [isTreeOpen, setIsTreeOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // 문서 이동 시 트리 접기 + 본문 상단으로
  useEffect(() => {
    setIsTreeOpen(false);
    contentRef.current?.scrollIntoView({ block: 'start' });
  }, [manualId, section, slug]);

  const tailSpacer = useTailSpacer(contentRef, `${manualId}/${section}/${slug}`);

  const content = !manualId ? (
    <ManualHome />
  ) : !manual ? (
    <NotFound what="매뉴얼" target={manualId} backTo="/manual" />
  ) : !section || !slug ? (
    <ManualCoverPage manual={manual} />
  ) : doc ? (
    <>
      <Breadcrumb manual={manual} docTitle={doc.title} />
      <ManualDoc doc={doc} />
    </>
  ) : (
    <NotFound what="문서" target={`${section}/${slug}`} backTo={`/manual/${manual.id}`} />
  );

  return (
    <div className="flex gap-8">
      {/* 좌: 현재 매뉴얼의 섹션 트리 (전체 목록 화면에서는 없음) */}
      {manual && (
        <div className={`${isTreeOpen ? 'block' : 'hidden'} w-56 flex-shrink-0 lg:block`}>
          <div className="sticky top-0 max-h-[calc(100vh-8rem)] overflow-y-auto pr-1">
            <SectionTree
              manual={manual}
              currentSection={section}
              currentSlug={slug}
              onNavigate={() => setIsTreeOpen(false)}
            />
          </div>
        </div>
      )}

      {/* 중앙: 본문 */}
      <div ref={contentRef} className="min-w-0 flex-1">
        <div className="mb-4 flex items-center gap-2">
          {manual && (
            <button
              type="button"
              onClick={() => setIsTreeOpen((open) => !open)}
              className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 lg:hidden"
              aria-expanded={isTreeOpen}
            >
              {isTreeOpen ? '목차 닫기' : '목차 보기'}
            </button>
          )}

          {/* 단축키를 모르는 사람도 찾을 수 있게 — 검색은 목차보다 넓은 진입로다. */}
          <button
            type="button"
            onClick={() => setIsSearchOpen(true)}
            className="flex flex-1 items-center gap-2 rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-50 sm:max-w-xs"
          >
            <span aria-hidden>🔍</span>
            <span>매뉴얼 검색</span>
            <span className="ml-auto rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-xs text-gray-400">
              {SEARCH_HOTKEY_HINT}
            </span>
          </button>
        </div>

        <ManualSearch
          open={isSearchOpen}
          onOpen={() => setIsSearchOpen(true)}
          onClose={() => setIsSearchOpen(false)}
        />

        {/* data-manual-body: 아래 여백을 뺀 '순수 본문' 높이를 재는 기준 (useTailSpacer) */}
        <div data-manual-body>{content}</div>

        {/* 마지막 제목이 목차 활성 띠까지 올라올 수 있게 하는 여백. 필요 없으면 0이라 렌더되지 않는다. */}
        {doc && tailSpacer > 0 && <div aria-hidden style={{ height: tailSpacer }} />}
      </div>

      {/* 우: 문서 내 목차 (좁은 폭에서는 숨김) */}
      {doc && (
        <div className="hidden w-56 flex-shrink-0 xl:block">
          <ManualToc containerRef={contentRef} docKey={`${manualId}/${section}/${slug}`} />
        </div>
      )}
    </div>
  );
}
